// Walk-in waitlist (F-RSV-N1): add a party with a quoted wait, "Table ready"
// opens a WhatsApp click-out (wa.me, prefilled — never a business-initiated
// template) and marks them notified, then Seat or Remove.
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageCircle, Plus, Trash2, Users } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { minutesSinceISO, pktTime, tableReadyMessage, waLink, type WaitlistEntry } from '@/lib/reservations'
import type { DiningTable } from '@/types'
import { SeatDialog, type SeatTarget } from './SeatDialog'

function AddPartyDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient()
  const { toast } = useToast()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [party, setParty] = useState('2')
  const [wait, setWait] = useState('15')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (open) {
      setName('')
      setPhone('')
      setParty('2')
      setWait('15')
      setNotes('')
      setError(null)
    }
  }, [open])
  const add = useMutation({
    mutationFn: async () => {
      const res = await apiClient.addWaitlistEntry({
        guest_name: name.trim(),
        guest_phone: phone.trim() || undefined,
        party_size: Number(party),
        quoted_wait_minutes: wait === '' ? undefined : Number(wait),
        notes: notes.trim() || undefined,
      })
      if (!res.success) throw new Error(res.message || 'Could not add the party')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reservations', 'waitlist'] })
      toast({ title: 'Added to the waitlist', description: name.trim() })
      onOpenChange(false)
    },
    onError: (e: Error) => setError(e.message),
  })
  const submit = () => {
    setError(null)
    if (!name.trim()) return setError("Enter the guest's name")
    const p = Number(party)
    if (!Number.isInteger(p) || p < 1 || p > 200) return setError('Party size must be 1 to 200')
    add.mutate()
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add to waitlist</DialogTitle>
          <DialogDescription>Take a number so you can message them when the table is ready.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="wl-name">Guest name</Label>
            <Input id="wl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sara Malik" autoFocus />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="wl-phone">Phone</Label>
            <Input id="wl-phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0333 7654321" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wl-party">Guests</Label>
            <Input id="wl-party" type="number" min={1} max={200} value={party} onChange={(e) => setParty(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wl-wait">Quoted wait (minutes)</Label>
            <Input id="wl-wait" type="number" min={0} max={600} value={wait} onChange={(e) => setWait(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="wl-notes">Notes</Label>
            <Textarea id="wl-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        {error ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button disabled={add.isPending} onClick={submit}>
            {add.isPending ? 'Adding…' : 'Add party'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function WaitlistTab({ tables, restaurantName }: { tables: DiningTable[]; restaurantName: string }) {
  const qc = useQueryClient()
  const { toast } = useToast()
  const [adding, setAdding] = useState(false)
  const [seating, setSeating] = useState<SeatTarget | null>(null)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  const { data, isLoading } = useQuery({
    queryKey: ['reservations', 'waitlist'],
    queryFn: () => apiClient.listWaitlist().then((r) => r.data),
    refetchInterval: 30_000,
  })
  const entries = data?.entries ?? []
  const active = entries.filter((e) => e.status === 'waiting' || e.status === 'notified')
  const done = entries.filter((e) => e.status === 'seated' || e.status === 'removed')

  const act = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'notify' | 'remove' }) => {
      const res = await apiClient.waitlistAction(id, action)
      if (!res.success) throw new Error(res.message || 'Could not update')
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reservations', 'waitlist'] }),
    onError: (e: Error) => toast({ title: 'Could not update', description: e.message, variant: 'destructive' }),
  })

  const tableReady = (e: WaitlistEntry) => {
    const href = waLink(e.guest_phone, tableReadyMessage(e, restaurantName))
    if (href) window.open(href, '_blank', 'noopener')
    else toast({ title: 'No WhatsApp number', description: 'Marked as told — let them know in person.' })
    act.mutate({ id: e.id, action: 'notify' })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {active.length === 0 ? 'Nobody is waiting.' : `${active.length} ${active.length === 1 ? 'party' : 'parties'} waiting`}
        </p>
        <Button onClick={() => setAdding(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add party
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-2">
          {active.map((e) => {
            const waited = minutesSinceISO(e.created_at, now)
            const late = e.quoted_wait_minutes != null && waited > e.quoted_wait_minutes
            return (
              <Card key={e.id} className="rounded-lg">
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{e.guest_name}</span>
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" aria-hidden /> {e.party_size}
                      </span>
                      {e.status === 'notified' ? <Badge variant="info-surface">Told table is ready</Badge> : null}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Waiting {waited} min
                      {e.quoted_wait_minutes != null ? ` · quoted ${e.quoted_wait_minutes} min` : ''}
                      {late ? <span className="font-medium text-destructive"> · over the quote</span> : null}
                      {e.guest_phone ? ` · ${e.guest_phone}` : ''}
                    </p>
                    {e.notes ? <p className="text-sm text-muted-foreground">{e.notes}</p> : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" disabled={act.isPending} onClick={() => tableReady(e)}>
                      <MessageCircle className="mr-2 h-4 w-4" /> Table ready
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        setSeating({
                          kind: 'waitlist',
                          id: e.id,
                          guestName: e.guest_name,
                          guestPhone: e.guest_phone,
                          partySize: e.party_size,
                          notes: e.notes,
                          tableIds: [],
                        })
                      }
                    >
                      Seat
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove ${e.guest_name}`}
                      disabled={act.isPending}
                      onClick={() => act.mutate({ id: e.id, action: 'remove' })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}
          {done.length > 0 ? (
            <div className="pt-2">
              <p className="mb-2 text-sm font-medium text-muted-foreground">Earlier today</p>
              <div className="divide-y rounded-lg border">
                {done.map((e) => (
                  <div key={e.id} className="flex items-center justify-between px-4 py-2 text-sm">
                    <span>
                      {e.guest_name} <span className="text-muted-foreground">({e.party_size})</span>
                    </span>
                    <span className="text-muted-foreground">
                      {e.status === 'seated'
                        ? `Seated${e.table_number ? ` at ${e.table_number}` : ''}${e.seated_at ? ` · ${pktTime(e.seated_at)}` : ''}`
                        : `Removed${e.removed_at ? ` · ${pktTime(e.removed_at)}` : ''}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      <AddPartyDialog open={adding} onOpenChange={setAdding} />
      <SeatDialog target={seating} tables={tables} onOpenChange={(o) => !o && setSeating(null)} />
    </div>
  )
}
